// Web auth via the Firebase JS SDK (the `firebase` package is installed and
// web-only here; native uses index.ts → @react-native-firebase/auth).

import {
  type Auth,
  type User,
  EmailAuthProvider,
  GoogleAuthProvider,
  OAuthProvider,
  signInAnonymously as fbSignInAnonymously,
  createUserWithEmailAndPassword,
  deleteUser,
  getAuth,
  linkWithCredential,
  linkWithPopup,
  onAuthStateChanged,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut as fbSignOut,
} from 'firebase/auth';

import { getFirebaseWebApp } from '../firebaseWeb';
import { AuthError, type AuthService, type AuthUser, mapAuthError } from './types';

let cached: Auth | null = null;

const getAuthInstance = (): Auth | null => {
  if (cached) return cached;
  const app = getFirebaseWebApp();
  if (!app) return null;
  cached = getAuth(app);
  return cached;
};

const requireAuth = (): Auth => {
  const a = getAuthInstance();
  if (!a) throw new AuthError('not-configured', 'Firebase web auth not configured');
  return a;
};

const toUser = (u: User): AuthUser => ({
  uid: u.uid,
  email: u.email,
  displayName: u.displayName,
  emailVerified: u.emailVerified,
  isAnonymous: u.isAnonymous,
  providerId: u.providerData[0]?.providerId ?? (u.isAnonymous ? 'anonymous' : 'password'),
});

export const isAvailable = (): boolean => getAuthInstance() !== null;

export const getCurrentUser = (): AuthUser | null => {
  const u = getAuthInstance()?.currentUser;
  return u ? toUser(u) : null;
};

export const onAuthChanged = (cb: (user: AuthUser | null) => void): (() => void) => {
  const a = getAuthInstance();
  if (!a) {
    cb(null);
    return () => {};
  }
  return onAuthStateChanged(a, (u) => cb(u ? toUser(u) : null));
};

export const signUpWithEmail = async (email: string, password: string): Promise<AuthUser> => {
  const a = requireAuth();
  try {
    // Upgrade an anonymous account in place so its data (alerts, applications)
    // survives the transition; otherwise create a fresh account.
    if (a.currentUser?.isAnonymous) {
      const cred = EmailAuthProvider.credential(email, password);
      const { user } = await linkWithCredential(a.currentUser, cred);
      return toUser(user);
    }
    const { user } = await createUserWithEmailAndPassword(a, email, password);
    return toUser(user);
  } catch (e) {
    throw mapAuthError(e);
  }
};

export const signInWithEmail = async (email: string, password: string): Promise<AuthUser> => {
  try {
    const { user } = await signInWithEmailAndPassword(requireAuth(), email, password);
    return toUser(user);
  } catch (e) {
    throw mapAuthError(e);
  }
};

const popupOrLink = async (a: Auth, provider: GoogleAuthProvider | OAuthProvider): Promise<AuthUser> => {
  try {
    // Link to an anonymous account so its data carries over.
    if (a.currentUser?.isAnonymous) {
      const { user } = await linkWithPopup(a.currentUser, provider);
      return toUser(user);
    }
    const { user } = await signInWithPopup(a, provider);
    return toUser(user);
  } catch (e) {
    // The provider account already exists → just sign into it (the anonymous
    // session is dropped, since that account already has its own data).
    if ((e as { code?: string })?.code === 'auth/credential-already-in-use') {
      const { user } = await signInWithPopup(a, provider);
      return toUser(user);
    }
    throw mapAuthError(e);
  }
};

export const signInWithGoogle = (): Promise<AuthUser> =>
  popupOrLink(requireAuth(), new GoogleAuthProvider());

export const signInWithApple = (): Promise<AuthUser> =>
  popupOrLink(requireAuth(), new OAuthProvider('apple.com'));

export const signInAnonymously = async (): Promise<AuthUser> => {
  try {
    const { user } = await fbSignInAnonymously(requireAuth());
    return toUser(user);
  } catch (e) {
    throw mapAuthError(e);
  }
};

export const sendPasswordReset = async (email: string): Promise<void> => {
  try {
    await sendPasswordResetEmail(requireAuth(), email);
  } catch (e) {
    throw mapAuthError(e);
  }
};

export const signOut = async (): Promise<void> => {
  try {
    await fbSignOut(requireAuth());
  } catch (e) {
    throw mapAuthError(e);
  }
};

export const deleteAccount = async (): Promise<void> => {
  try {
    const u = requireAuth().currentUser;
    if (u) await deleteUser(u);
  } catch (e) {
    throw mapAuthError(e);
  }
};

const service: AuthService = {
  isAvailable,
  getCurrentUser,
  onAuthChanged,
  signUpWithEmail,
  signInWithEmail,
  signInWithGoogle,
  signInWithApple,
  signInAnonymously,
  sendPasswordReset,
  signOut,
  deleteAccount,
};

export default service;
